"use client";

import { cn } from "@/lib/utils";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useState } from "react";
import { Toaster, toast } from "sonner";
import { useRouter } from "next/navigation";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useRegisterCompany, handleAuthError } from "@/lib/api-hooks";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { CalendarIcon } from "lucide-react";
import { Calendar } from "../ui/calendar";

// Country options (you can expand this list)
const COUNTRIES = ["KENYA"];

// Define form schema with Zod
const signupFormSchema = z.object({
  company_name: z.string().min(2, "Company name must be at least 2 characters"),
  company_email: z.string().email("Please enter a valid company email"),
  country_of_incorporation: z.string().min(1, "Please select a country"),
  company_pin: z.string().min(1, "Company PIN/Tax ID is required"),
  date_of_incorporation: z.string().min(1, "Date of incorporation is required"),
  employee_count: z.coerce.number().min(1, "Employee count must be at least 1"),

  admin_email: z.string().email("Please enter a valid admin email"),
  admin_first_name: z
    .string()
    .min(2, "First name must be at least 2 characters")
    .max(30, "First name must be at most 30 characters"),
  admin_last_name: z
    .string()
    .min(2, "Last name must be at least 2 characters")
    .max(30, "Last name must be at most 30 characters"),
  admin_middle_name: z
    .string()
    .min(2, "Middle name must be at least 2 characters")
    .max(30, "Middle name must be at most 30 characters")
    .optional()
    .or(z.literal("").transform(() => undefined)),
  admin_phone: z
    .string()
    .min(6, "Phone number must be at least 6 characters")
    .max(18, "Phone number must be at most 18 characters")
    .optional()
    .or(z.literal("").transform(() => undefined)),
});

type SignupFormValues = z.infer<typeof signupFormSchema>;

export function SignupForm({
  className,
  ...props
}: React.ComponentProps<"div">) {
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [companyUniqueId, setCompanyUniqueId] = useState("");
  const router = useRouter();

  const registerMutation = useRegisterCompany();

  // Initialize form
  const form = useForm<SignupFormValues>({
    // @ts-ignore
    resolver: zodResolver(signupFormSchema),
    defaultValues: {
      company_name: "",
      company_email: "",
      country_of_incorporation: "",
      company_pin: "",
      date_of_incorporation: "",
      employee_count: 10,

      admin_email: "",
      admin_first_name: "",
      admin_last_name: "",
      admin_middle_name: "",
      admin_phone: "",
    },
  });

  // Handle form submission
  const onSubmit = async (data: SignupFormValues) => {
    try {
      const response = await registerMutation.mutateAsync({
        ...data,
        date_of_incorporation: new Date(
          data.date_of_incorporation
        ).toISOString(),
        admin_username: data.admin_email,
        company_alias: data.company_name
      });

      setCompanyUniqueId(response?.company?.uniqueId);
      setRegistrationSuccess(true);
      toast.success("Company registered successfully!");
    } catch (error: any) {
      const errorMessage = handleAuthError(error);
      toast.error(errorMessage);
    }
  };

  const goToLogin = () => router.push("/login");

  if (registrationSuccess) {
    return (
      <div className={cn("flex flex-col gap-6", className)} {...props}>
        <Toaster richColors />
        <Card className="overflow-hidden">
          <CardContent className="p-8">
            <div className="flex flex-col gap-6 text-center">
              <div className="flex flex-col items-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                  <svg
                    className="w-8 h-8 text-green-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
                <h1 className="text-2xl font-bold text-kaziniBlue">
                  Registration Successful!
                </h1>
                <p className="text-balance text-kaziniMuted">
                  Your company has been registered successfully
                </p>
              </div>

              <div className="text-sm text-kaziniMuted">
                <p>
                  A confirmation email has been sent to{" "}
                  <strong>{form.getValues("admin_email")}</strong> with your
                  company details and login instructions.
                </p>
              </div>

              <Button
                onClick={goToLogin}
                className="w-full bg-kaziniBlue hover:bg-kaziniBlueLight"
              >
                Continue to Login
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className={cn("flex flex-col gap-6", className)} {...props}>
      <Toaster richColors />
      <Card className="overflow-hidden">
        <CardContent className="grid p-0 md:grid-cols-2">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="p-6 md:p-8">
              <div className="flex flex-col gap-6">
                <div className="flex flex-col items-center text-center">
                  <h1 className="text-2xl font-bold text-kaziniBlue">
                    Register Your Company
                  </h1>
                  <p className="text-balance text-kaziniMuted">
                    Enter your company details to get started
                  </p>
                </div>

                {/* Company Information */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-kaziniBlue">
                    Company Information
                  </h3>

                  <div className="grid gap-4">
                    <FormField
                      control={form.control}
                      name="company_name"
                      render={({ field }) => (
                        <FormItem className="grid gap-2 text-kaziniBlue">
                          <FormLabel>Company Name</FormLabel>
                          <FormControl>
                            <Input placeholder="Acme Corporation" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <FormField
                    control={form.control}
                    name="country_of_incorporation"
                    render={({ field }) => (
                      <FormItem className="grid gap-2 text-kaziniBlue">
                        <FormLabel>Country of Incorporation</FormLabel>
                        <Select
                          onValueChange={field.onChange}
                          defaultValue={field.value}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select a country" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {COUNTRIES.map((country) => (
                              <SelectItem key={country} value={country}>
                                {country.replace(/_/g, " ")}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="grid gap-4 md:grid-cols-2">
                    <FormField
                      control={form.control}
                      name="company_pin"
                      render={({ field }) => (
                        <FormItem className="grid gap-2 text-kaziniBlue">
                          <FormLabel>Company PIN/Tax ID</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="Tax identification number"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="date_of_incorporation"
                      render={({ field }) => (
                        <FormItem className="grid gap-2 text-kaziniBlue">
                          <FormLabel>Date of Incorporation</FormLabel>
                          <Popover>
                            <PopoverTrigger asChild>
                              <FormControl>
                                <Button
                                  variant={"outline"}
                                  className={cn(
                                    "w-[240px] pl-3 text-left font-normal",
                                    !field.value && "text-muted-foreground"
                                  )}
                                >
                                  {field.value ? (
                                    format(field.value, "PPP")
                                  ) : (
                                    <span>Pick a date</span>
                                  )}
                                  <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                                </Button>
                              </FormControl>
                            </PopoverTrigger>
                            <PopoverContent
                              className="w-auto p-0"
                              align="start"
                            >
                              <Calendar
                                mode="single"
                                selected={
                                  field.value
                                    ? new Date(field.value)
                                    : undefined
                                }
                                onSelect={(date) => {
                                  field.onChange(
                                    date ? date.toISOString() : ""
                                  );
                                }}
                                disabled={(date) =>
                                  date > new Date() ||
                                  date < new Date("1900-01-01")
                                }
                                captionLayout="dropdown"
                              />
                            </PopoverContent>
                          </Popover>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="grid gap-4 md:grid-cols-2">
                    <FormField
                      control={form.control}
                      name="company_email"
                      render={({ field }) => (
                        <FormItem className="grid gap-2 text-kaziniBlue">
                          <FormLabel>Company Email</FormLabel>
                          <FormControl>
                            <Input
                              type="email"
                              placeholder="info@company.com"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="employee_count"
                      render={({ field }) => (
                        <FormItem className="grid gap-2 text-kaziniBlue">
                          <FormLabel>Employee Count</FormLabel>
                          <FormControl>
                            <Input
                              type="number"
                              min="1"
                              placeholder="10"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>

                {/* Admin Information */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-kaziniBlue">
                    Administrator Information
                  </h3>

                  <FormField
                    control={form.control}
                    name="admin_email"
                    render={({ field }) => (
                      <FormItem className="grid gap-2 text-kaziniBlue">
                        <FormLabel>Admin Email</FormLabel>
                        <FormControl>
                          <Input
                            type="email"
                            placeholder="admin@company.com"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="grid gap-4 md:grid-cols-2">
                    <FormField
                      control={form.control}
                      name="admin_first_name"
                      render={({ field }) => (
                        <FormItem className="grid gap-2 text-kaziniBlue">
                          <FormLabel>Admin First Name</FormLabel>
                          <FormControl>
                            <Input placeholder="John" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="admin_last_name"
                      render={({ field }) => (
                        <FormItem className="grid gap-2 text-kaziniBlue">
                          <FormLabel>Admin Last Name</FormLabel>
                          <FormControl>
                            <Input placeholder="Smith" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="admin_middle_name"
                      render={({ field }) => (
                        <FormItem className="grid gap-2 text-kaziniBlue">
                          <FormLabel>Admin Middle Name (optional)</FormLabel>
                          <FormControl>
                            <Input placeholder="Michael" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="admin_phone"
                      render={({ field }) => (
                        <FormItem className="grid gap-2 text-kaziniBlue">
                          <FormLabel>Admin Phone (optional)</FormLabel>
                          <FormControl>
                            <Input
                              type="tel"
                              placeholder="+1234567890"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full bg-kaziniBlue hover:bg-kaziniBlueLight"
                  disabled={registerMutation.isPending}
                >
                  {registerMutation.isPending
                    ? "Registering..."
                    : "Register Company"}
                </Button>

                <div className="text-center text-sm text-kaziniBlue">
                  Already have an account?{" "}
                  <a href="/login" className="underline underline-offset-4">
                    Sign in
                  </a>
                </div>
              </div>
            </form>
          </Form>

          <div className="relative hidden md:block flex justify-center m-auto">
            <img
              src="/images/kazini-hr-original-colors.png"
              alt="Image"
              className="w-[70%] m-auto"
            />
          </div>
        </CardContent>
      </Card>
      <div className="text-balance text-center text-xs text-muted-foreground [&_a]:underline [&_a]:underline-offset-4 hover:[&_a]:text-primary">
        By clicking continue, you agree to our <a href="#">Terms of Service</a>{" "}
        and <a href="#">Privacy Policy</a>.
      </div>
    </div>
  );
}
